/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: Results
 * File     : Results.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the Results view and coordinating data fetching.
 *   Exports: Results
 *   
 *
 * Dependencies:
 *   - ../components/layout/Ads
 *   - ../context/AuthContext
 *   - ../services/api
 *   - @mui/icons-material/Lock
 *   - @mui/material
 *   - react
 *   - react-router-dom
 *   - socket.io-client
 *
 * Usage:
 *   Use in Router: <Route path="/results" element={<Results />} />
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Box, LinearProgress, Paper, Stack, Button, Avatar, Rating, Chip, IconButton } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import { AdSlot } from '../components/layout/Ads';
import { getResults, fetchPoll, submitRating, getMovieByImdb, getMovieByTitle, getClubComments, addClubComment, api, buildApiUrl } from '../services/api';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';

const Results = () => {
  const { pollId } = useParams();
  const [poll, setPoll] = useState(null);
  const [results, setResults] = useState({ totalVotes: 0, results: [] });
  const [resultsLoading, setResultsLoading] = useState(true);
  const [myRating, setMyRating] = useState(null);
  const [ratingError, setRatingError] = useState('');
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const [omdb, setOmdb] = useState({});
  // Drawer state (movie info like Global Metrics)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState(null); // { title, year, poster, imdbID }
  const [omdbFull, setOmdbFull] = useState({ _for: null, data: null });
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    setResultsLoading(true);
    // Fetch vote counts immediately with cache-bust so we don't show stale data (e.g. after voting)
    getResults(pollId, { cacheBust: true })
      .then(setResults)
      .catch(() => setResults({ totalVotes: 0, results: [] }))
      .finally(() => setResultsLoading(false));
    fetchPoll(pollId)
      .then(async (p) => {
        setPoll(p);
        if (typeof p.myRating === 'number') {
          setMyRating(p.myRating);
        }
        const details = {};
        const extractImdb = (val) => {
          if (!val || typeof val !== 'string') return null;
          const m = val.match(/(tt\d{3,8})/i);
          return m ? m[1] : null;
        };
        for (const m of p.movies || []) {
          const id = extractImdb(m.imdbID);
          if (id) {
            try {
              details[id] = await getMovieByImdb(id);
            } catch (_) {}
          }
        }
        setOmdb(details);
      })
      .catch(() => {});
  }, [pollId]);

  useEffect(() => {
    // Live updates via Socket.IO — disabled when backend is Cloudflare Workers (no persistent connections)
    if ((process.env.REACT_APP_SOCKET_IO_ENABLED || 'false').toLowerCase() === 'false') return;
    const base = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || (window.location.origin.replace(':3000', ':5000'));
    const socket = io(base, {
      path: '/api/socket.io',
      withCredentials: true,
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
    });
    socket.on('connect_error', () => {
      socket.io.opts.reconnection = false;
      socket.disconnect();
    });
    socket.emit('join', { pollId });
    socket.on('vote:cast', (payload) => {
      if (payload?.pollId === pollId) {
        getResults(pollId).then(setResults).catch(() => {});
      }
    });
    return () => socket.disconnect();
  }, [pollId]);

  const eligibleAt = () => {
    if (!poll || !poll.winner || !poll.winner.movie) return null;
    const end = new Date(poll.endDate).getTime();
    const runtime = poll.winner.movie.runtimeMinutes || 0;
    return end + runtime * 60000;
  };
  const ratingEligible = () => {
    const at = eligibleAt();
    return at !== null && Date.now() >= at;
  };

  const isAdminOrOwner = !!user && (user.role === 'admin' || user.role === 'owner');
  const openMovieDrawer = (movie) => {
    if (!movie) return;
    setSelectedMovie(movie);
    setDrawerOpen(true);
    
    const imdbID = movie.imdbID || null;
    const searchKey = imdbID || `${movie.title}-${movie.year}`;
    
    // Only fetch data if we haven't already loaded it for this movie
    if (omdbFull._for !== searchKey) {
      if (imdbID) {
        // Use IMDB ID if available
        getMovieByImdb(imdbID)
          .then((d) => setOmdbFull({ _for: searchKey, data: d || null }))
          .catch(() => setOmdbFull({ _for: searchKey, data: null }));
      } else {
        // Use title and year search as fallback
        getMovieByTitle(movie.title, movie.year)
          .then((d) => setOmdbFull({ _for: searchKey, data: d || null }))
          .catch(() => setOmdbFull({ _for: searchKey, data: null }));
      }
    }
  };

  useEffect(() => {
    if (!poll || !poll.clubId || !user) return;
    if (!ratingEligible()) return;
    getClubComments(poll.clubId, pollId).then(r => setComments(r.comments || [])).catch(() => setComments([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poll, user]);

  const onRate = async (_, value) => {
    if (!value) return;
    setRatingError('');
    setSaving(true);
    try {
      await submitRating(poll.id, poll.winner.movie.id, value);
      setMyRating(value);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setRatingError('You have already rated this film.');
        // Fetch their actual saved rating so the UI reflects reality
        try {
          const fresh = await fetchPoll(poll.id);
          if (typeof fresh.myRating === 'number') setMyRating(fresh.myRating);
        } catch (_) {}
      } else if (status === 403) {
        setRatingError('Only voters can rate this film.');
      } else {
        setRatingError('Rating failed. Please try again.');
      }
    }
    setSaving(false);
  };

  const requiresHostPlan = !!user && user.role !== 'owner' && user.planStatus !== 'active';

  const goSubscribe = async () => {
    try {
      const r = await api.post('/billing/checkout');
      if (r?.data?.url) window.location.href = r.data.url;
    } catch (_) {}
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 0 } }}>
      <Typography variant="h4" gutterBottom>Results</Typography>
      {poll && (
        <>
          <Typography variant="h6" gutterBottom>{poll.title}</Typography>
          {poll.club ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" gutterBottom>Club: {poll.club.name}</Typography>
              {poll.club.hostInactive ? (
                <Chip color="warning" size="small" label="Host inactive: new polls locked" />
              ) : null}
            </Stack>
          ) : (poll.clubNameArchived ? (
            <Typography variant="caption" color="text.secondary" gutterBottom>{poll.clubNameArchived}</Typography>
          ) : null)}
          {poll.creator ? (
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Created by {poll.creator.displayName || poll.creator.username}
            </Typography>
          ) : null}
        </>
      )}
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {resultsLoading ? 'Loading results…' : `Total votes: ${results.totalVotes}`}
      </Typography>
      {poll?.winner?.movie && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {ratingEligible() ? 'Ratings are now open.' : (() => {
            const at = eligibleAt();
            if (!at) return null;
            const remainingMs = Math.max(0, at - Date.now());
            const hh = Math.floor(remainingMs / 3600000);
            const mm = Math.floor((remainingMs % 3600000) / 60000);
            const ss = Math.floor((remainingMs % 60000) / 1000);
            return `Ratings open in ${hh}h ${mm}m ${ss}s`;
          })()}
        </Typography>
      )}
      <Stack spacing={2}>
        <AdSlot slot="0000000002" />
        {resultsLoading ? (
          <Typography color="text.secondary">Loading vote counts…</Typography>
        ) : (
        results.results.filter(r => r && r.movieId).map(r => {
          const movie = poll?.movies?.find(m => m.id === r.movieId) || {};
          const imdbID = (movie.imdbID && (movie.imdbID.match(/(tt\d{3,8})/i)?.[1])) || null;
          const d = imdbID ? omdb[imdbID] || {} : {};
          const duration = d.Runtime && d.Runtime !== 'N/A' ? d.Runtime : (movie.runtimeMinutes ? `${movie.runtimeMinutes} min` : null);
          const genres = d.Genre && d.Genre !== 'N/A' ? d.Genre : null;
          const imdbRating = d.imdbRating && d.imdbRating !== 'N/A' ? d.imdbRating : null;
          const imdbVotes = d.imdbVotes && d.imdbVotes !== 'N/A' ? d.imdbVotes : null;
          return (
          <Paper key={r.movieId} sx={{ p: 2, cursor: 'pointer' }} onClick={() => openMovieDrawer({ title: r.title, year: movie.year, poster: r.poster, imdbID })}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar variant="rounded" src={r.poster && r.poster !== 'N/A' ? (r.poster.startsWith('http') ? r.poster : buildApiUrl(`/api/plex/image?path=${encodeURIComponent(r.poster)}`)) : undefined} alt={`${r.title} poster`} sx={{ width: 40, height: 56 }} />
              <Box flex={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography>
                    {imdbID ? (
                      <a href={`https://www.imdb.com/title/${imdbID}/`} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                        {r.title}
                      </a>
                    ) : r.title}
                  </Typography>
                  <Typography>{r.votes} votes ({r.percentage}%)</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {[
                    duration || undefined,
                    genres || undefined,
                    (imdbRating || imdbVotes) ? `IMDb ${imdbRating || '-'}${imdbVotes ? ` (${imdbVotes})` : ''}` : undefined,
                  ].filter(Boolean).join(' • ')}
                </Typography>
                <LinearProgress variant="determinate" value={r.percentage} sx={{ mt: 1 }} />
              </Box>
            </Stack>
          </Paper>
        );}) )}
      </Stack>
      {user && ratingEligible() && (
        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <Typography gutterBottom>
            {myRating ? `You rated this ${myRating}/5` : `Rate the winner: ${poll?.winner?.movie?.title || 'Winner'}`}
          </Typography>
          <Rating
            name="rate-movie"
            value={myRating || 0}
            onChange={onRate}
            max={5}
            precision={0.5}
            disabled={saving || myRating !== null}
            sx={{ '& .MuiRating-icon': { fontSize: 48 } }}
          />
          {ratingError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {ratingError}
            </Typography>
          )}
        </Box>
      )}
      {/* Movie info drawer - Custom implementation to avoid aria-hidden issues */}
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
            }}
            onClick={() => setDrawerOpen(false)}
          />
          {/* Drawer Panel */}
          <Box
            sx={{
              position: 'fixed',
              top: { xs: '56px', sm: '64px' },
              right: 0,
              bottom: 0,
              width: { xs: '100%', md: 420 },
              backgroundColor: 'background.paper',
              boxShadow: 16,
              zIndex: 1301,
              overflow: 'auto',
              p: 2,
            }}
          >
        {selectedMovie ? (
          <Box>
            <Box sx={{ position: 'sticky', top: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{selectedMovie.title} {selectedMovie.year ? `(${selectedMovie.year})` : ''}</Typography>
              <IconButton 
                aria-label="close" 
                onClick={() => setDrawerOpen(false)} 
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            {(() => {
              const imdbID = selectedMovie.imdbID;
              const d = omdbFull._for === (imdbID || `${selectedMovie.title}-${selectedMovie.year}`) ? omdbFull.data : null;
              const posterUrl = d?.Poster && d.Poster !== 'N/A' ? d.Poster : 
                               (selectedMovie.poster && selectedMovie.poster.startsWith('http') ? selectedMovie.poster : 
                                (selectedMovie.poster ? buildApiUrl(`/api/plex/image?path=${encodeURIComponent(selectedMovie.poster)}`) : null));
              
              return posterUrl ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <img src={posterUrl} alt={selectedMovie.title} style={{ maxWidth: '100%', borderRadius: 8 }} />
                </Box>
              ) : null;
            })()}
            {(() => {
              const imdbID = selectedMovie.imdbID;
              const searchKey = imdbID || `${selectedMovie.title}-${selectedMovie.year}`;
              const d = omdbFull._for === searchKey ? omdbFull.data : null;
              
              // Show basic movie info even without OMDB data
              if (!d) {
                return (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Basic movie information available. Additional details may be loaded if IMDB data is available.
                    </Typography>
                    {selectedMovie.year && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Year: </strong>{selectedMovie.year}
                      </Typography>
                    )}
                  </Box>
                );
              }
              const toPeopleLinks = (csv) => (csv || '')
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .map((name, idx) => (
                  <Button key={`p-${idx}`} size="small" variant="text" sx={{ px: 0.5, minWidth: 0 }}
                    href={`https://www.imdb.com/find/?q=${encodeURIComponent(name)}&s=nm`} target="_blank" rel="noreferrer">
                    {name}
                  </Button>
                ));
              return (
                <Box sx={{ mb: 1 }}>
                  {d.Plot && d.Plot !== 'N/A' && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {d.Plot}
                    </Typography>
                  )}
                  {(d.Director && d.Director !== 'N/A') && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Director: </strong>{toPeopleLinks(d.Director)}
                    </Typography>
                  )}
                  {(d.Actors && d.Actors !== 'N/A') && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Cast: </strong>{toPeopleLinks(d.Actors)}
                    </Typography>
                  )}
                  {(d.imdbRating || d.imdbVotes) && (
                    <Chip label={`IMDb ${d.imdbRating || '-'}${d.imdbVotes ? ` (${d.imdbVotes})` : ''}`} size="small" sx={{ mr: 1 }} />
                  )}
                  {isAdminOrOwner && selectedMovie.poster && selectedMovie.poster !== 'N/A' && !String(selectedMovie.poster).startsWith('http') && (
                    <Chip label="On Plex" color="success" size="small" />
                  )}
                </Box>
              );
            })()}
          </Box>
        ) : null}
          </Box>
        </>
      )}
      {poll?.clubId && ratingEligible() && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>Club Discussion</Typography>
          {requiresHostPlan && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Chip color="warning" size="small" icon={<LockIcon fontSize="small" />} label="Host plan required to participate in discussions" />
              <Button size="small" onClick={goSubscribe}>Subscribe</Button>
            </Stack>
          )}
          {comments.filter(c => c && c.id).map((c, idx) => (
            <Paper key={c.id} sx={{ p: 1.5, mb: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2">
                  {c.user?.username || 'User'} {c.user?.role ? `(${c.user.role})` : ''}
                  {typeof c.userRating === 'number' && (
                    <>
                      {' '}• Rated {c.userRating}/5
                    </>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(c.createdAt).toLocaleString()}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{c.message}</Typography>
              {c.userAggregate?.count ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Avg rating by {c.user?.username || 'user'}: {c.userAggregate.avg}/5 ({c.userAggregate.count} total)
                </Typography>
              ) : null}
            </Paper>
          ))}
          {!requiresHostPlan && user ? (
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <input style={{ flex: 1, borderRadius: 6, padding: '8px 10px' }} placeholder="Share your thoughts…" value={newComment} onChange={(e) => setNewComment(e.target.value)} />
              <Button variant="contained" onClick={async () => {
                if (!newComment.trim()) return;
                try {
                  await addClubComment({ clubId: poll.clubId, pollId, message: newComment.trim() });
                  setNewComment('');
                  const r = await getClubComments(poll.clubId, pollId);
                  setComments(r.comments || []);
                } catch (_) {}
              }}>Post</Button>
            </Box>
          ) : null}
        </Box>
      )}
      <AdSlot slot="0000000003" />
      <Button sx={{ mt: 2 }} href={`${window.location.origin}/vote?poll=${pollId}`}>
        Share vote link
      </Button>
    </Box>
  );
};

export default Results;


